import { useAccount } from "@qw/auth";
import {
  type CreatePlaybookInput,
  File_Audience,
  File_Source,
  type PlaybookQuery,
  useCreatePlaybookMutation,
  useDeletePlaybookMutation,
  useImportPlaybookFromUserMutation,
  useMyPlaybooksQuery,
  usePlaybookQuery,
  useSubscribeToPlaybookMutation,
  useUnsubscribeFromPlaybookMutation,
  useUpdatePlaybookMutation,
} from "@qw/graphql";
import { PlaybookType } from "@qw/qw-common";
import {
  AutosaveIndicator,
  AutosaveWrapper,
  Button,
  ButtonGroup,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  FieldSelect,
  FieldTextarea,
  Form,
  Group,
  Icon,
  Separator,
  Spinner,
  toast,
  UnsavedChangesDialog,
  uploadFiles,
  useAutosaveHandler,
  useNavigationBlocking,
  useOpenConfirmation,
} from "@qw/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  type BaseSyntheticEvent,
  type FC,
  type Ref,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { usePage } from "@/providers/use-page";
import { AuthoringEditorPanes } from "../../components/authoring-editor-panes";
import { useLaunchDarklyIdentified } from "../../launch-darkly-identify";
import { PlaybookAuthor } from "./playbook-author";
import { PlaybookBackButton } from "./playbook-back-button";
import { DescriptionCharCount } from "./playbook-description-char-count";
import {
  DESCRIPTION_MAX_LENGTH,
  MODEL_TIER_RUNTIME_DEFAULT,
  PlaybookFormSchema,
  type PlaybookFormValues,
} from "./playbook-editor.types";
import { PlaybookEditorChatPanel } from "./playbook-editor-chat-panel";
import { PlaybookFiles, type PlaybookFilesProps } from "./playbook-files";
import { PlaybookModelTierField } from "./playbook-model-tier-field";
import { PlaybookNameField } from "./playbook-name-field";
import { PlaybookSetsField } from "./playbook-sets-field";
import { PlaybookSubscribers } from "./playbook-subscribers";
import { PlaybookTemplateParamDeclarer, TemplateAuthoringValidationSummary } from "./playbook-template-param-declarer";
import {
  getDeclaredTemplateParams,
  getSaveBlockingTemplateAuthoringWarnings,
  hasTemplateMarkup,
  parseTemplateParamSchema,
  type TemplateParamDefinition,
  validateTemplateAuthoring,
} from "./playbook-template-params";
import { PlaybookToolFilterField } from "./playbook-tool-filter-field";
import { PlaybookWorkflowEditor, type PlaybookWorkflowEditorHandle } from "./playbook-workflow-editor";

/**
 * `templateAuthoring`, latched for the lifetime of the mounted editor: once a user
 * is granted access it stays granted until they navigate away. LaunchDarkly can
 * retarget mid-session, and reacting live would yank the editor out from under an
 * author — unmounting the form (dropping unsaved edits along with the autosave and
 * leave-protection that would have caught them), or letting a save persist a type
 * the UI had just stopped offering. Access is re-evaluated on the next mount.
 */
const useLatchedTemplateAuthoring = (): boolean => {
  const flags = useFlags<Record<string, boolean | undefined>>();
  const isEnabledNow = flags.templateAuthoring === true;
  const [wasEverEnabled, setWasEverEnabled] = useState(isEnabledNow);

  useEffect(() => {
    if (isEnabledNow) {
      setWasEverEnabled(true);
    }
  }, [isEnabledNow]);

  return wasEverEnabled;
};

export const PlaybookEditor: FC = () => {
  const params = useParams();
  const { user } = useAccount();
  const navigate = useNavigate();
  const isTemplateAuthoringEnabled = useLatchedTemplateAuthoring();
  // `templateAuthoring` only reads true once LaunchDarkly has identified the
  // signed-in user; until then every flag sits at its anonymous default-off
  // value. Treat a `false` reading as authoritative only after identify settles,
  // so a pilot who deep-links or reloads a template URL isn't bounced to
  // /playbooks in the race window before LD re-evaluates.
  const isLaunchDarklyIdentified = useLaunchDarklyIdentified();

  const playbookId = params.playbookId ?? null;

  const {
    data: playbookData,
    loading,
    refetch: refetchPlaybook,
  } = usePlaybookQuery({
    variables: { id: playbookId as string },
    skip: !playbookId,
  });

  const playbook = playbookData?.playbook ?? null;
  const isExternalPlaybook = playbook?.userId !== user.id;
  const isTemplatePlaybook = playbook?.type === PlaybookType.Template;

  useEffect(() => {
    if (isTemplatePlaybook && !isTemplateAuthoringEnabled && isLaunchDarklyIdentified) {
      toast({
        title: "Email template editor is not available yet",
        description: "This template is hidden from the playbook editor until authoring support ships.",
      });
      navigate("/playbooks", { replace: true });
    }
  }, [isLaunchDarklyIdentified, isTemplateAuthoringEnabled, isTemplatePlaybook, navigate]);

  return (
    <>
      {loading && !playbook && <Spinner />}
      {/* A loaded template with the flag not-yet-true and LD not-yet-identified is
          in the race window: hold on a spinner rather than rendering the editor or
          redirecting until identify settles. */}
      {playbook && isTemplatePlaybook && !isTemplateAuthoringEnabled && !isLaunchDarklyIdentified && <Spinner />}
      {playbook && (!isTemplatePlaybook || isTemplateAuthoringEnabled) && (
        <PlaybookForm canEdit={!isExternalPlaybook} playbook={playbook} refetchPlaybook={refetchPlaybook} />
      )}
    </>
  );
};

export interface PlaybookFormProps {
  playbook: PlaybookQuery["playbook"];
  canEdit: boolean;
  refetchPlaybook: () => Promise<unknown>;
}

interface PlaybookEditorContentPaneProps {
  canEdit: boolean;
  workflowEditorRef: Ref<PlaybookWorkflowEditorHandle>;
  templateParams: TemplateParamDefinition[];
  onTemplateParamsChange: (params: TemplateParamDefinition[]) => void;
  getDeclaredParams: () => TemplateParamDefinition[];
  currentWorkflowHtml: string;
  onWorkflowHtmlChange: (html: string) => void;
  isPaused: boolean;
}

/**
 * The settings that only mean something when Alby *executes* a playbook: what
 * parameters an invocation passes, which tools the run may use, and which model it
 * runs at. An Email Template is filled deterministically — no tools, no model — so
 * showing these would be noise at best. Worse, the tool panel asserts "All N tools
 * are available to this playbook", which is simply untrue of a template. Swap the
 * group for one line that says why it's absent, rather than leaving disabled
 * controls making false claims.
 *
 * Lives in its own component because it reads the live, form-watched type
 * (PlaybookForm's body sits outside the form provider).
 */
const PlaybookExecutionSettings: FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const selectedType = useWatch<PlaybookFormValues, "type">({ name: "type" });

  if (selectedType === PlaybookType.Template) {
    return (
      <div className="rounded-md border border-dashed px-3 py-2.5 text-xs text-muted-foreground">
        Email templates are filled in field by field, so they never call tools or run a model. Execution settings don't
        apply here.
      </div>
    );
  }

  return (
    <>
      <FieldTextarea
        label="Input Parameters"
        name="parametersDescription"
        placeholder="e.g., { policyNumber: string, effectiveDate: string }"
        disabled={!canEdit}
        readOnly={!canEdit}
      />

      <Collapsible className="-mt-4">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer">
          <Icon icon="info" className="h-3.5 w-3.5" />
          <span>About input parameters</span>
          <Icon icon="keyboard_arrow_down" className="h-4 w-4" />
        </CollapsibleTrigger>
        <CollapsibleContent className="text-xs text-muted-foreground mt-2 pl-5 space-y-1.5">
          <p>
            When invoked via <code className="bg-muted px-1 rounded">executePlaybook</code>, parameters are passed as a
            JSON object (key-value pairs). This field describes what parameters to expect—use plain English, a schema,
            or any format that helps Alby understand what to pass.
          </p>
          <p>
            <strong>Limitation:</strong> Auto-triggered playbooks (e.g., inbox classification) cannot receive
            parameters—only explicit <code className="bg-muted px-1 rounded">executePlaybook</code> calls.
          </p>
        </CollapsibleContent>
      </Collapsible>

      <PlaybookToolFilterField canEdit={canEdit} />

      <PlaybookModelTierField canEdit={canEdit} />
    </>
  );
};

/**
 * Attachments belong to a playbook Alby executes — it reads them for context and can
 * reference them mid-run. An Email Template is filled field by field into an email
 * body; nothing consumes an attached file, and the picker even describes them as
 * files "to reference in your workflow". Hide it rather than offer an upload that
 * goes nowhere.
 *
 * Hidden, not cleared: a playbook retyped to Template keeps whatever files it had,
 * so switching back restores them instead of silently discarding an upload.
 *
 * Sets, by contrast, stay visible for a Template — a set is how one canonical
 * template is shared with a desk (see the template-sharing change to the set
 * repository), so the picker is exactly how an author puts it there.
 */
const PlaybookFilesUnlessTemplate: FC<PlaybookFilesProps> = (props) => {
  const selectedType = useWatch<PlaybookFormValues, "type">({ name: "type" });

  if (selectedType === PlaybookType.Template) {
    return null;
  }

  return <PlaybookFiles {...props} />;
};

/**
 * Renders the name field, optional template-authoring UI, and the workflow editor.
 * Lives inside the form provider so it can derive template mode from the live,
 * react-hook-form-watched `type` value rather than the loaded playbook type — so
 * selecting "Email Template" immediately reveals slot/param authoring and switching
 * away hides it.
 */
const PlaybookEditorContentPane: FC<PlaybookEditorContentPaneProps> = ({
  canEdit,
  workflowEditorRef,
  templateParams,
  onTemplateParamsChange,
  getDeclaredParams,
  currentWorkflowHtml,
  onWorkflowHtmlChange,
  isPaused,
}) => {
  const selectedType = useWatch<PlaybookFormValues, "type">({ name: "type" });
  const isTemplateEditing = selectedType === PlaybookType.Template;
  const { setValue } = useFormContext<PlaybookFormValues>();

  // Mirror param edits into react-hook-form (this component is inside the form
  // provider; PlaybookForm's body is not). Autosave and leave protection read RHF
  // state, so without this a param-only edit — adding, removing, or renaming a
  // field without touching any other input — never marks the form dirty and is
  // lost on navigation.
  const handleParamsChange = useCallback(
    (params: TemplateParamDefinition[]) => {
      onTemplateParamsChange(params);
      setValue("templateParamSchema", params, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [onTemplateParamsChange, setValue]
  );

  const templateValidationWarnings = useMemo(
    () =>
      isTemplateEditing
        ? validateTemplateAuthoring({
            bodyHtml: currentWorkflowHtml,
            declaredParams: templateParams,
          })
        : [],
    [currentWorkflowHtml, isTemplateEditing, templateParams]
  );

  return (
    <div className="pr-10 flex-1 min-w-0 flex flex-col">
      <PlaybookNameField canEdit={canEdit} />
      {isTemplateEditing && (
        <div className="mb-3 space-y-3">
          <PlaybookTemplateParamDeclarer disabled={!canEdit} params={templateParams} onChange={handleParamsChange} />
          <TemplateAuthoringValidationSummary warnings={templateValidationWarnings} />
        </div>
      )}
      <PlaybookWorkflowEditor
        ref={workflowEditorRef}
        editable={canEdit}
        content={currentWorkflowHtml}
        // Always pass the live getter, never conditionally: the editor instance is
        // kept alive across type flips, so a form that mounted as a Workflow would
        // otherwise stay configured with empty params and the `{` slot menu would
        // come up blank after switching to Template. Mode is enforced by the
        // insertion gate inside the extension, not by withholding the params.
        getDeclaredParams={getDeclaredParams}
        isTemplateEditing={isTemplateEditing}
        isPaused={isPaused}
        onWorkflowHtmlChange={onWorkflowHtmlChange}
      />
    </div>
  );
};

export const PlaybookForm: FC<PlaybookFormProps> = ({ canEdit, playbook, refetchPlaybook }) => {
  const { user } = useAccount();
  const { setChatPanel, setIsChatOpen } = usePage();
  const navigate = useNavigate();
  const params = useParams();
  // Latched: see useLatchedTemplateAuthoring — a mid-session flag flip must not
  // change what this open editor offers or persists.
  const isTemplateAuthoringEnabled = useLatchedTemplateAuthoring();
  const { openConfirmation } = useOpenConfirmation();
  const { data: myPlaybooksData } = useMyPlaybooksQuery();

  const [createPlaybook] = useCreatePlaybookMutation();
  const [updatePlaybook] = useUpdatePlaybookMutation();
  const [deletePlaybook, { loading: isDeleting }] = useDeletePlaybookMutation();
  const [importPlaybook, { loading: isImporting }] = useImportPlaybookFromUserMutation();
  const [subscribe, { loading: isSubscribing }] = useSubscribeToPlaybookMutation();
  const [unsubscribe, { loading: isUnsubscribing }] = useUnsubscribeFromPlaybookMutation();

  const playbookId = params.playbookId ?? null;

  const isExternalPlaybook = playbook.userId !== user.id;
  const isSubscribed = useMemo(
    () => playbook.subscribers?.some((s) => s.id === user.id),
    [playbook.subscribers, user.id]
  );
  const myImportedIds = useMemo(
    () => new Set((myPlaybooksData?.myPlaybooks ?? []).map((p) => p.copiedFromPlaybookId).filter(Boolean)),
    [myPlaybooksData]
  );
  const isImported = isExternalPlaybook && playbookId ? myImportedIds.has(playbookId) : false;

  const externalVersion = isExternalPlaybook ? (playbook?.updatedAt ?? "loading") : "";
  const editorKey = `${playbookId ?? "new"}:${isExternalPlaybook ? "external" : "owned"}:${externalVersion}`;

  const defaultValues: PlaybookFormValues = {
    name: playbook.name ?? "",
    description: playbook.description ?? "",
    parametersDescription: playbook.parametersDescription ?? "",
    workflow: playbook.workflow ?? "",
    type: (playbook.type as PlaybookType | undefined) ?? PlaybookType.Workflow,
    toolFilter: (playbook.toolFilter ?? null) as PlaybookFormValues["toolFilter"],
    modelTier: playbook.modelTier ?? MODEL_TIER_RUNTIME_DEFAULT,
    files:
      playbook.files?.map((f) => ({
        id: f.fileUploadId,
        filename: f.filename,
        size: f.size,
        mimeType: f.mimeType,
      })) ?? [],
    templateParamSchema: parseTemplateParamSchema(playbook.templateParamSchema),
  };

  const workflowEditorRef = useRef<PlaybookWorkflowEditorHandle>(null);
  const closedChatForPlaybookIdRef = useRef<string | null>(null);
  const [currentWorkflowHtml, setCurrentWorkflowHtml] = useState(defaultValues.workflow);
  const [templateParams, setTemplateParams] = useState<TemplateParamDefinition[]>(() =>
    parseTemplateParamSchema(playbook.templateParamSchema)
  );
  const templateParamsRef = useRef(templateParams);
  const hydratedTemplateParamsKeyRef = useRef<string | null>(null);

  useEffect(() => {
    setCurrentWorkflowHtml(defaultValues.workflow);
  }, [defaultValues.workflow, editorKey]);

  useEffect(() => {
    // Rehydrate declared params from the server only when switching to a different
    // playbook/version (editorKey) — NOT on every refetch. refetchPlaybook() runs
    // after each successful save, and blindly re-parsing playbook.templateParamSchema
    // then would overwrite rows the user kept editing while the save was in flight.
    if (hydratedTemplateParamsKeyRef.current === editorKey) {
      return;
    }
    hydratedTemplateParamsKeyRef.current = editorKey;
    const parsedTemplateParams = parseTemplateParamSchema(playbook.templateParamSchema);
    templateParamsRef.current = parsedTemplateParams;
    setTemplateParams(parsedTemplateParams);
  }, [editorKey, playbook.templateParamSchema]);

  const handleTemplateParamsChange = useCallback((params: TemplateParamDefinition[]) => {
    templateParamsRef.current = params;
    setTemplateParams(params);
  }, []);

  const getDeclaredParams = useCallback(() => getDeclaredTemplateParams(templateParamsRef.current), []);

  const playbookTypeOptions = useMemo(
    () => [
      ...(isTemplateAuthoringEnabled
        ? [
            {
              label: "Email Template",
              value: PlaybookType.Template,
              description:
                "Use to author reusable email body templates with declared fillable fields and conditional sections.",
            },
          ]
        : []),
      {
        label: "Instructional Prompt Workflow",
        value: PlaybookType.Workflow,
        description:
          "Use to describe repetitive actions (e.g., processing a quote or drafting an email) so you can prompt Alby to run through them for you.",
      },
      {
        label: "Contextual Background Knowledge",
        value: PlaybookType.Knowledge,
        description:
          "Use for background knowledge you want Alby to know all the time (e.g., account preferences, or information specific to your workflows).",
      },
      {
        label: "Workflow Step",
        value: PlaybookType.WorkflowStep,
        description:
          "A step inside a larger workflow. Only invoked when another playbook references it by ID. Hidden from playbook discovery.",
      },
    ],
    [isTemplateAuthoringEnabled]
  );

  const insertHtmlAtCursor = useCallback((html: string) => {
    workflowEditorRef.current?.insertContentAtCursor(html);
  }, []);

  const authoringChatPanel = useMemo(() => {
    if (!canEdit || !playbookId) {
      return null;
    }

    return (
      <PlaybookEditorChatPanel
        currentWorkflowHtml={currentWorkflowHtml}
        onInsert={insertHtmlAtCursor}
        playbookId={playbookId}
      />
    );
  }, [canEdit, currentWorkflowHtml, insertHtmlAtCursor, playbookId]);

  useEffect(() => {
    setChatPanel(authoringChatPanel);
  }, [authoringChatPanel, setChatPanel]);

  useEffect(() => {
    return () => setChatPanel(null);
  }, [setChatPanel]);

  useLayoutEffect(() => {
    if (playbookId && closedChatForPlaybookIdRef.current !== playbookId) {
      setIsChatOpen(false);
      closedChatForPlaybookIdRef.current = playbookId;
    }
  }, [playbookId, setIsChatOpen]);

  const handleSubmit = useCallback(
    async (values: PlaybookFormValues, _methods?: unknown, event?: BaseSyntheticEvent) => {
      if (values.description && values.description.length > DESCRIPTION_MAX_LENGTH) {
        throw new Error(`Description must be ${DESCRIPTION_MAX_LENGTH} characters or less`);
      }

      const isTemplateSave = values.type === PlaybookType.Template;

      if (isTemplateSave) {
        const saveBlockingWarnings = getSaveBlockingTemplateAuthoringWarnings(
          validateTemplateAuthoring({
            bodyHtml: values.workflow,
            declaredParams: templateParamsRef.current,
          })
        );

        if (saveBlockingWarnings.length > 0) {
          setCurrentWorkflowHtml(values.workflow);
          if (!event) {
            throw new Error("Fix the blocking template field issues before saving.");
          }
          return;
        }
      } else if (hasTemplateMarkup(values.workflow)) {
        // Reverse conversion: a Template retyped to Workflow/Knowledge keeps its
        // slots and conditionals, which are inert outside a template — workflow
        // consumers flatten the body to text, so slots would read as literal param
        // names and a conditional's contents would always be included. Block with an
        // explanation (the template validation summary isn't rendered in this mode)
        // rather than silently stripping the author's content.
        toast({
          title: "Remove the template fields first",
          description:
            "This body still contains template slots or conditional blocks. They don't work outside an Email Template — remove them, then change the type.",
        });
        if (!event) {
          throw new Error("Remove template slots and conditional blocks before changing the playbook type.");
        }
        return;
      }

      const input = {
        name: values.name,
        description: values.description,
        parametersDescription: values.parametersDescription || null,
        workflow: values.workflow,
        type: values.type,
        toolFilter: values.toolFilter,
        // Always send a tier, never omit it: an omitted field leaves the column untouched,
        // so "Default" could not clear a tier the playbook already declares.
        modelTier: values.modelTier === MODEL_TIER_RUNTIME_DEFAULT ? null : values.modelTier,
        fileUploadIds: values.files.map((f) => f.id),
        ...(isTemplateSave
          ? {
              templateParamSchema: getDeclaredTemplateParams(
                templateParamsRef.current
              ) as CreatePlaybookInput["templateParamSchema"],
            }
          : {}),
      };

      if (!playbookId) {
        const created = await createPlaybook({
          variables: { input },
          refetchQueries: ["Playbook", "MyPlaybooks", "MyInvokablePlaybooks"],
        });
        const newId = created.data?.createPlaybook?.id;
        if (newId) {
          navigate(`/playbooks/${newId}`);
        }
        return;
      }

      // Refetch MyInvokablePlaybooks on update so retyping a playbook into or
      // out of Workflow propagates to the slash menu, chat empty-state,
      // trigger picker, and classification picker without a page reload.
      await updatePlaybook({
        variables: { id: playbookId, input },
        refetchQueries: ["MyInvokablePlaybooks"],
      });
      await refetchPlaybook();
    },
    [playbookId, createPlaybook, updatePlaybook, navigate, refetchPlaybook]
  );

  const { isAutosavePaused, setIsAutosavePaused, saveStatus, handleAutosave, shouldWarnBeforeLeaving } =
    useAutosaveHandler({
      entityId: playbookId,
      canEdit,
      handleSubmit,
    });

  const blocker = useNavigationBlocking(shouldWarnBeforeLeaving);

  const handleUnsubscribe = useCallback(async () => {
    if (!playbookId) {
      return;
    }
    try {
      await unsubscribe({
        variables: { playbookId },
        refetchQueries: ["MySubscribedPlaybooks", "MyInvokablePlaybooks"],
      });
      await refetchPlaybook();
    } catch (error) {
      toast({
        title: "Cannot unsubscribe",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  }, [unsubscribe, playbookId, refetchPlaybook]);

  const handleSubscribe = useCallback(async () => {
    if (!playbookId) {
      return;
    }
    await subscribe({
      variables: { playbookId },
      refetchQueries: ["MySubscribedPlaybooks", "MyInvokablePlaybooks"],
    });
    await refetchPlaybook();
  }, [subscribe, playbookId, refetchPlaybook]);

  const handleCreateCopy = useCallback(async () => {
    if (!playbookId) {
      return;
    }

    const { data } = await importPlaybook({
      variables: { playbookId },
      refetchQueries: ["MyPlaybooks", "MyInvokablePlaybooks"],
    });
    if (data?.importPlaybookFromUser) {
      navigate(`/playbooks/${data.importPlaybookFromUser.id}`);
    }
  }, [importPlaybook, playbookId, navigate]);

  const handleReimport = useCallback(async () => {
    if (!playbookId) {
      return;
    }

    const confirmed = await openConfirmation({
      title: `Re-import ${playbook.name || "(untitled)"}`,
      description: (
        <>
          <strong>This will overwrite your existing playbook with the latest version from the author.</strong> Are you
          sure you want to continue?
        </>
      ),
      buttonProps: {
        theme: "destructive",
        children: isImporting ? "Re-importing" : "Re-import",
        disabled: isImporting,
      },
    });

    if (!confirmed) {
      return;
    }

    const { data } = await importPlaybook({
      variables: { playbookId },
      refetchQueries: ["MyPlaybooks", "MyInvokablePlaybooks"],
    });
    if (data?.importPlaybookFromUser) {
      navigate(`/playbooks/${data.importPlaybookFromUser.id}`);
    }
  }, [importPlaybook, playbookId, navigate, openConfirmation, playbook.name, isImporting]);

  const handleDelete = useCallback(async () => {
    if (!playbookId) {
      return;
    }

    const confirmed = await openConfirmation({
      title: "Delete playbook",
      description: (
        <>
          Are you sure you want to delete <strong>{playbook.name || "(untitled)"}</strong>? This action cannot be
          undone.
        </>
      ),
      buttonProps: { theme: "destructive", children: isDeleting ? "Deleting..." : "Delete", disabled: isDeleting },
    });

    if (!confirmed) {
      return;
    }

    await deletePlaybook({
      variables: { id: playbookId },
      refetchQueries: ["MyPlaybooks", "MyInvokablePlaybooks"],
    });
    navigate("/playbooks");
  }, [deletePlaybook, isDeleting, navigate, openConfirmation, playbookId, playbook.name]);

  const handleUploadFiles = useCallback(async (files: File[]) => {
    const { fileData } = await uploadFiles(files, {
      audience: File_Audience.Internal,
      source: File_Source.ManualUpload,
    });
    return fileData;
  }, []);

  return (
    <Form
      key={editorKey}
      mode="onChange"
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      validationSchema={PlaybookFormSchema}
      className="flex flex-auto min-h-0 min-w-0 w-full bg-card overflow-hidden"
    >
      <AuthoringEditorPanes
        autoSaveId="playbook-editor-panes"
        detailsLabel="playbook details"
        panelIds={{ editor: "playbook-editor", details: "playbook-details" }}
        details={({ collapseButton }) => (
          <div className="p-6">
            <Group type="flex" className="gap-6">
              {canEdit && playbookId ? (
                <>
                  <AutosaveIndicator
                    isAutosavePaused={isAutosavePaused}
                    setIsAutosavePaused={setIsAutosavePaused}
                    saveStatus={saveStatus}
                    trailingAction={collapseButton}
                    updatedAt={playbook.updatedAt}
                  />
                  <Separator orientation="horizontal" />
                </>
              ) : (
                <div className="flex justify-end">{collapseButton}</div>
              )}

              <div className="flex flex-col gap-1.5">
                <FieldTextarea
                  label="Description"
                  name="description"
                  placeholder="Short description (optional). Will be used by Alby to decide if this playbook is relevant to the current conversation."
                  disabled={!canEdit}
                  readOnly={!canEdit}
                />
                <DescriptionCharCount />
              </div>

              <FieldSelect
                label="How should Alby use this playbook?"
                name="type"
                disabled={!canEdit}
                readOnly={!canEdit}
                options={playbookTypeOptions}
              />

              <PlaybookExecutionSettings canEdit={canEdit} />

              {canEdit && playbookId && <PlaybookSetsField playbookId={playbookId} />}

              <PlaybookFilesUnlessTemplate canEdit={canEdit} handleUploadFiles={handleUploadFiles} />

              <div className="flex items-center justify-between">
                <PlaybookAuthor author={playbook.author} />
                {!!playbook.subscribers?.length && <PlaybookSubscribers subscribers={playbook.subscribers} />}
              </div>

              <Separator orientation="horizontal" />

              <ButtonGroup direction="col" className="items-stretch">
                {!canEdit && playbookId && (
                  <>
                    {isSubscribed ? (
                      <Button variant="secondary" size="sm" onClick={handleUnsubscribe} disabled={isUnsubscribing}>
                        <Icon icon="group_remove" className="text-xs mr-1" />
                        Unsubscribe
                      </Button>
                    ) : (
                      <Button size="sm" onClick={handleSubscribe} disabled={isSubscribing}>
                        <Icon icon="how_to_reg" className="text-xs" />
                        Subscribe
                      </Button>
                    )}

                    {isImported ? (
                      <Button disabled={isImporting} variant="outline" size="sm" onClick={handleReimport}>
                        Re-import playbook
                      </Button>
                    ) : (
                      <Button onClick={handleCreateCopy} disabled={isImporting} variant="outline" size="sm">
                        <Icon icon="file_copy" />
                        Create a copy
                      </Button>
                    )}

                    {playbook.copiedFromPlaybookId && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => navigate(`/playbooks/${playbook.copiedFromPlaybookId}`)}
                        className="my-1 text-sm text-muted-foreground"
                      >
                        View source playbook
                      </Button>
                    )}
                  </>
                )}

                {canEdit && (
                  <Button size="sm" variant="outline" title="Delete" disabled={isDeleting} onClick={handleDelete}>
                    <Icon icon="delete" />
                    Delete
                  </Button>
                )}
              </ButtonGroup>
            </Group>
          </div>
        )}
      >
        <div className="flex h-full min-h-0 min-w-0 gap-4 overflow-y-auto overflow-x-hidden p-5 pl-4">
          <PlaybookBackButton />

          <PlaybookEditorContentPane
            canEdit={canEdit}
            workflowEditorRef={workflowEditorRef}
            templateParams={templateParams}
            onTemplateParamsChange={handleTemplateParamsChange}
            getDeclaredParams={getDeclaredParams}
            currentWorkflowHtml={currentWorkflowHtml}
            onWorkflowHtmlChange={setCurrentWorkflowHtml}
            isPaused={isAutosavePaused}
          />
        </div>
      </AuthoringEditorPanes>

      {playbookId && canEdit && (
        <AutosaveWrapper<PlaybookFormValues> onSave={handleAutosave} isPaused={isAutosavePaused} />
      )}

      <UnsavedChangesDialog blocker={blocker} />
    </Form>
  );
};
